import {
  MATRIX_DESTINATAIRES,
  MATRIX_DESTINATAIRE_AUTRE_MAX_LENGTH,
  MATRIX_DESTINATAIRE_LABELS,
  MATRIX_DESTINATAIRE_NONE_LABEL,
  type MatrixDestinataire,
  matrixDestinataireSchema,
  resolveDestinataireValue,
} from '@mss_creator/shared';
import { OptionTile, TextField } from '@pms-digital/design-system';
import { useEffect, useId, useRef } from 'react';

const AUTRE_PLACEHOLDER = "Les fournisseurs de l'entreprise";

/** The radio value of « Non défini »: the recipient itself is null, a radio needs a string. */
const NONE_VALUE = 'non_defini';

export interface DestinataireFieldProps {
  /** Null = « Non défini ». */
  value: MatrixDestinataire | null;
  /** The free text of « Autre », as typed. Shown only while `value` is `autre`. */
  autre: string;
  /** Frozen with the paliers, or locked while a generation runs. */
  disabled: boolean;
  onChange: (next: MatrixDestinataire | null) => void;
  onAutreChange: (next: string) => void;
}

/**
 * Who receives the action the matrix describes (issues #21 and #25, planche
 * 19): a group of radio tiles, « Non défini » first and by default, a free-text
 * line that opens under it with « Autre » (and takes the focus), and a help
 * line that spells out what the model receives, so the choice reads as what
 * it becomes. « Autre » left empty is not an error: it means « Non défini ».
 */
export function DestinataireField({
  value,
  autre,
  disabled,
  onChange,
  onAutreChange,
}: DestinataireFieldProps) {
  const baseId = useId();
  const legendId = `${baseId}-legend`;
  const autreId = `${baseId}-autre-text`;
  const autreInputRef = useRef<HTMLInputElement>(null);
  const focusAutreRef = useRef(false);

  const isAutre = value === 'autre';
  const sent = resolveDestinataireValue({ destinataire: value, destinataireAutre: autre });

  // Focus follows the choice, once the input is on screen.
  useEffect(() => {
    if (isAutre && focusAutreRef.current) {
      focusAutreRef.current = false;
      autreInputRef.current?.focus();
    }
  }, [isAutre]);

  function handleValueChange(next: string) {
    if (next === NONE_VALUE) {
      onChange(null);
      return;
    }
    const parsed = matrixDestinataireSchema.safeParse(next);
    if (!parsed.success) return;
    if (parsed.data === 'autre' && !isAutre) focusAutreRef.current = true;
    onChange(parsed.data);
  }

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend id={legendId} className="font-semibold text-small">
        Destinataire
      </legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <OptionTile
          type="radio"
          name={baseId}
          value={NONE_VALUE}
          label={MATRIX_DESTINATAIRE_NONE_LABEL}
          checked={value === null}
          disabled={disabled}
          onChange={() => handleValueChange(NONE_VALUE)}
        />
        {MATRIX_DESTINATAIRES.map((destinataire) => (
          <OptionTile
            key={destinataire}
            type="radio"
            name={baseId}
            value={destinataire}
            label={MATRIX_DESTINATAIRE_LABELS[destinataire]}
            checked={value === destinataire}
            disabled={disabled}
            onChange={() => handleValueChange(destinataire)}
          />
        ))}
      </div>
      {isAutre && (
        <div className="max-w-120">
          <TextField
            ref={autreInputRef}
            id={autreId}
            type="text"
            label={<span>Destinataire, saisie libre</span>}
            value={autre}
            maxLength={MATRIX_DESTINATAIRE_AUTRE_MAX_LENGTH}
            placeholder={AUTRE_PLACEHOLDER}
            disabled={disabled}
            onChange={(event) => onAutreChange(event.target.value)}
          />
        </div>
      )}
      <p className="text-gray-600 text-small">
        {sent === null ? (
          'Les puces décrivent ce que fait la personne, sans destinataire précisé.'
        ) : (
          <>
            Les puces décrivent ce que fait la personne, à l'égard de :{' '}
            <em className="font-semibold text-noir not-italic">{sent}</em>.
          </>
        )}
      </p>
    </fieldset>
  );
}
